import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { Tenant, TenantOpening } from "src/app/backend/models/tenant/tenant";
import { UserNotify } from "src/app/support/notify-decorator";
import { environment } from "src/environments/environment";
import { RegionCode } from "google-libphonenumber";
import { Timezone } from "../../support/localitation";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import {
  omitFalsyProperties,
  omitUndefinedAndNullProperties
} from "src/app/support/object";
import { Member } from "src/app/backend/models/member/member";
import { HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID } from "src/app/core/http-context-tokens";

@Injectable()
export class TenantBackend {
  private readonly http = inject(HttpClient);

  @UserNotify({
    successMessage: marker("Utente invitato"),
    errorMessage: marker("Impossibile invitare l'utente")
  })
  invite({
    accountId,
    tenantId,
    email,
    roles
  }: {
    accountId: string;
    tenantId: string;
    email: string;
    roles: string[];
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.iam}/accounts/${accountId}/tenants/${tenantId}/members`,
      {
        email,
        roles
      }
    );
  }

  loadMembers({
    page,
    size,
    accountId,
    tenantId
  }: {
    accountId: string;
    tenantId: string;
    page?: number;
    size?: number;
  }): Observable<PaginatedResult<Member>> {
    return this.http.get<PaginatedResult<Member>>(
      `${environment.snoots.api.iam}/accounts/${accountId}/tenants/${tenantId}/members`,
      {
        params: omitFalsyProperties({
          p: page,
          s: size
        })
      }
    );
  }

  @UserNotify({
    successMessage: marker("Tenant creato"),
    errorMessage: marker("Impossibile creare il tenant")
  })
  create(
    accountId: string,
    tenant: Partial<Tenant> & { region?: string }
  ): Observable<Tenant> {
    return this.http.post<Tenant>(
      `${environment.snoots.api.organization}/accounts/${accountId}/tenants`,
      tenant,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }

  search(
    request: PageRequest,
    account?: string
  ): Observable<PaginatedResult<Tenant>> {
    return this.http.get<PaginatedResult<Tenant>>(
      `${environment.snoots.api.organization}/tenants`,
      {
        params: omitUndefinedAndNullProperties({
          p: request.page,
          s: request.pageSize,
          account: account,
          ...request.filterValues
        })
      }
    );
  }

  load(id: string): Observable<Tenant> {
    return this.http.get<Tenant>(
      `${environment.snoots.api.organization}/tenants/${id}`
    );
  }

  loadPublic(id: string): Observable<Tenant> {
    return this.http.get<Tenant>(
      `${environment.snoots.api.organization}/tenants/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Tenant aggiornato"),
    errorMessage: marker("Impssibile aggiornare il tenant")
  })
  update(id: string, partial: Partial<Tenant>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/tenants/${id}`,
      omitFalsyProperties(partial)
    );
  }

  @UserNotify({
    successMessage: marker("Orari aggiornati"),
    errorMessage: marker("Impssibile aggiornare gli orari")
  })
  updateOpenings(id: string, openings: TenantOpening[]): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/tenants/${id}`,
      {
        openings
      }
    );
  }

  @UserNotify({
    successMessage: marker("Impostazioni aggiornate"),
    errorMessage: marker("Impssibile aggiornare le impostazioni")
  })
  updateTenantLocalitation(
    id: string,
    body: {
      region: RegionCode;
      locale: "it-IT" | "en-US" | "es-ES";
      timezone: Timezone;
    }
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.organization}/tenants/${id}`,
      body
    );
  }

  @UserNotify({
    successMessage: marker("Tenant rimosso"),
    errorMessage: marker("Impossibile rimuovere il tenant")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.organization}/tenants/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Tenant abilitato"),
    errorMessage: marker("Impossibile abilitare il tenant")
  })
  enable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.organization}/tenants/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Tenant disabilitato"),
    errorMessage: marker("Impossibile disabilitare il tenant")
  })
  disable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.organization}/tenants/${id}/disable`,
      {}
    );
  }
}
